// Lamp posts, zebra crossings, traffic lights and street glow.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { PI } from '../lib/math.js';
import { rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, canvasTex } from '../lib/factory.js';
import { instanced, instancedRaw } from '../lib/instancing.js';
import { streets } from './landmarks.js';
import { pits } from './trees.js';
import { lightPhase } from '../rules/traffic-light.js';

export let paintMat, grateTex, updateTrafficLights, lampHeadMat, glowMat;

export function buildStreetFurniture() {
  updateTrafficLights = function updateTrafficLights(time) {
    // a 14 s cycle per junction (offset across the grid): one direction green 6 s, amber 1 s, then the other way
    const key = Math.floor(time * 2);
    if (key === tlState) return;
    tlState = key;
    const m = new THREE.Matrix4(),
      p = new THREE.Vector3(),
      q = new THREE.Quaternion(),
      one = new THREE.Vector3(1, 1, 1),
      none = new THREE.Vector3(0, 0, 0);
    tl.forEach((t, i) => {
      const lamp = lightPhase(t.j, t.ns, time);
      tlLit.forEach((im, k) => {
        im.getMatrixAt(i, m);
        m.decompose(p, q, new THREE.Vector3());
        m.compose(p, q, k === lamp ? one : none);
        im.setMatrixAt(i, m);
        im.instanceMatrix.needsUpdate = true;
      });
    });
  };

  // Barcelona lamp posts on the chamfered corners, plus zebra crossings and lane dashes
  const lamps = [],
    zebras = [],
    dashes = [];
  for (const ix of streets)
    for (const iz of streets) {
      for (const [sx, sz] of [
        [1, 1],
        [1, -1],
        [-1, 1],
        [-1, -1],
      ])
        lamps.push({ x: ix + sx * 5.4, z: iz + sz * 5.4 });
      for (const s of [-1, 1])
        for (let k = -2; k <= 2; k++) {
          zebras.push({ x: ix + k * 0.8, z: iz + s * 7.4, rot: 0 });
          zebras.push({ x: ix + s * 7.4, z: iz + k * 0.8, rot: PI / 2 });
        }
    }
  for (const c of streets)
    for (let t = -45; t <= 45; t += 3) {
      if (streets.some((k) => Math.abs(t - k) < 9)) continue;
      dashes.push({ x: c, z: t, rot: 0 }, { x: t, z: c, rot: PI / 2 });
    }
  const wornTex = canvasTex(64, 64, (c) => {
    c.fillStyle = '#f2efe8';
    c.fillRect(0, 0, 64, 64);
    for (let k = 0; k < 500; k++) {
      c.fillStyle = `rgba(120,116,110,${rr(0.2, 0.7)})`;
      c.fillRect(rnd() * 64, rnd() * 64, rr(1, 3), rr(1, 2));
    }
  });
  paintMat = M(0xffffff, { map: wornTex, roughness: 0.7, flatShading: false });
  grateTex = canvasTex(64, 64, (c) => {
    c.fillStyle = '#2c2a27';
    c.fillRect(0, 0, 64, 64);
    c.strokeStyle = '#5b5751';
    c.lineWidth = 3;
    for (let r = 6; r < 32; r += 7) {
      c.beginPath();
      c.arc(32, 32, r, 0, PI * 2);
      c.stroke();
    }
    for (let a = 0; a < 12; a++) {
      c.beginPath();
      c.moveTo(32 + Math.cos((a * PI) / 6) * 6, 32 + Math.sin((a * PI) / 6) * 6);
      c.lineTo(32 + Math.cos((a * PI) / 6) * 31, 32 + Math.sin((a * PI) / 6) * 31);
      c.stroke();
    }
  });
  instanced(
    new THREE.BoxGeometry(0.8, 0.04, 0.8),
    [
      M(0x3a3633),
      M(0x3a3633),
      P(0xffffff, { map: grateTex, roughness: 0.5, metalness: 0.7 }),
      M(0x3a3633),
      M(0x3a3633),
      M(0x3a3633),
    ],
    pits,
    (o, p) => o.position.set(p.x, 0.37, p.z),
  );
  // Traffic lights: one per approach to each junction, on the right-hand kerb just before the zebra crossing, facing
  // the traffic that arrives (driving on the right). Approaches only exist where the street carries on beyond the
  // junction, so the outer ring gets none from the sea side. The two directions alternate green / amber / red.
  const tl = [];
  for (const ix of streets)
    for (const iz of streets)
      for (const [dx, dz] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        // (dx, dz) is the direction of travel of cars arriving at the junction; they come from the opposite side
        if (!streets.includes(ix - dx * 30) || !streets.includes(iz - dz * 30)) continue;
        const rx = -dz,
          rz = dx; // right-hand side of the direction of travel
        tl.push({
          x: ix - dx * 8.4 + rx * 2.75,
          z: iz - dz * 8.4 + rz * 2.75,
          ry: Math.atan2(-dx, -dz),
          ns: dz !== 0,
          j: (ix + iz) / 30,
        });
      }
  instanced(
    new THREE.CylinderGeometry(0.06, 0.08, 2.6, 6).translate(0, 1.3, 0),
    M(0x2b2f33),
    tl,
    (o, t) => o.position.set(t.x, 0.3, t.z),
  );
  // three-lens head with a visor over each lens, plus a small pedestrian head on the kerb side facing the crossing
  const headG = mergeGeometries([
    new THREE.BoxGeometry(0.26, 0.72, 0.2),
    ...[0.24, 0, -0.24].map((y) =>
      new THREE.BoxGeometry(0.2, 0.03, 0.12).translate(0, y + 0.1, 0.14),
    ),
  ]);
  instanced(headG, M(0x1c1e21, { flatShading: false }), tl, (o, t) => {
    o.position.set(t.x, 2.75, t.z);
    o.rotation.y = t.ry;
  });
  instanced(
    new THREE.BoxGeometry(0.18, 0.36, 0.16),
    M(0x1c1e21, { flatShading: false }),
    tl,
    (o, t) => {
      o.position.set(t.x, 2.0, t.z);
      o.rotation.y = t.ry + PI / 2;
    },
  );
  const lensAt = (t, y, fwd = 0.11) => ({
    x: t.x + Math.sin(t.ry) * fwd,
    y,
    z: t.z + Math.cos(t.ry) * fwd,
  });
  instanced(
    new THREE.SphereGeometry(0.07, 8, 6),
    M(0x2a2c2e, { roughness: 0.3 }),
    tl.flatMap((t) => [2.99, 2.75, 2.51].map((y) => lensAt(t, y))),
    (o, l) => o.position.set(l.x, l.y, l.z),
  ).castShadow = false;
  // the lit lenses: one instanced mesh per colour, an instance is hidden by scaling it to nothing
  const tlLit = [
    [0xff3b2f, 2.99],
    [0xffb02e, 2.75],
    [0x37d67a, 2.51],
  ].map(([c, y]) => {
    const im = instancedRaw(
      new THREE.SphereGeometry(0.075, 8, 6),
      M(c, { emissive: c, emissiveIntensity: 1.1 }),
      tl,
      (o, t) => {
        const l = lensAt(t, y, 0.115);
        o.position.set(l.x, l.y, l.z);
      },
    );
    im.castShadow = false;
    scene.add(im);
    return im;
  });
  let tlState = -1;
  instanced(new THREE.BoxGeometry(0.42, 0.03, 1.7), paintMat, zebras, (o, z) => {
    o.position.set(z.x, 0.015, z.z);
    o.rotation.y = z.rot;
  });
  instanced(new THREE.BoxGeometry(0.12, 0.03, 1.2), paintMat, dashes, (o, d) => {
    o.position.set(d.x, 0.015, d.z);
    o.rotation.y = d.rot;
  });
  instanced(
    new THREE.CylinderGeometry(0.07, 0.11, 3.4, 6).translate(0, 1.7, 0),
    M(0x2c3a33),
    lamps,
    (o, l) => o.position.set(l.x, 0.3, l.z),
  );
  lampHeadMat = M(0xfff4d6, { emissive: 0xffc46b, emissiveIntensity: 0, flatShading: false });
  instanced(new THREE.SphereGeometry(0.24, 10, 8), lampHeadMat, lamps, (o, l) =>
    o.position.set(l.x, 3.85, l.z),
  );
  const glowTex = canvasTex(64, 64, (c) => {
    const g = c.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,200,120,.9)');
    g.addColorStop(1, 'rgba(255,200,120,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, 64, 64);
  });
  glowMat = new THREE.MeshBasicMaterial({
    map: glowTex,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    opacity: 0,
  });
  const glows = instanced(new THREE.PlaneGeometry(6, 6).rotateX(-PI / 2), glowMat, lamps, (o, l) =>
    o.position.set(l.x, 0.34, l.z),
  );
  glows.castShadow = glows.receiveShadow = false;
}
